/**
 * How the site asks for analytics and advertising consent.
 *
 * One switch, read by every part that depends on it, so the behaviour and the
 * words on /privacy cannot disagree:
 *
 *   - scripts/consentModeHead.ts — the Google Consent Mode default in <head>
 *   - src/lib/ads.ts — whether the banner is shown, and what an unanswered
 *     visitor counts as (`effectiveConsent`)
 *   - src/pages/Tools.tsx (PrivacyPage) — the "Who is asked" section
 *
 * `strict` (the behaviour since 2026-09-18): every signal starts denied for
 * every visitor worldwide, and everyone sees the banner. Nothing that needs
 * consent happens until the visitor presses Allow.
 *
 * `regional`: Google's tags start denied in the EEA, the UK and Switzerland
 * (Consent Mode's `region` default, which Google resolves from the visitor's
 * IP address) and granted everywhere else. The banner is shown only to
 * visitors this browser places in those regions. Everyone else is opted in
 * until they say otherwise, and can opt out at any time with "Cookie choices"
 * in the footer of every page. Do Not Track and Global Privacy Control still
 * count as a refusal everywhere.
 *
 * A static site has no server to geolocate a visitor, so the banner decision in
 * `regional` mode uses the browser's time zone (`inConsentRegion`). It is
 * deliberately over-inclusive: every `Europe/*` zone counts, including
 * countries outside the EEA, so the error is asking someone who did not need
 * asking, not skipping someone who did.
 *
 * Changing the mode changes the inline Consent Mode script, so the build will
 * write a new script-src hash into public/.htaccess (scripts/generate-csp.ts).
 * That file must be deployed with it.
 */

export type ConsentMode = 'strict' | 'regional'

// TODO(owner): choose the mode after legal review. `strict` is the current,
// conservative behaviour and stays the default until then.
export const CONSENT_MODE: ConsentMode = 'strict'

/**
 * ISO 3166-1 alpha-2 codes for Consent Mode's `region` default: the 27 EU
 * member states, the three other EEA states (Iceland, Liechtenstein, Norway),
 * the United Kingdom and Switzerland.
 */
export const CONSENT_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
  'GB', 'CH',
] as const

/**
 * Time zones outside `Europe/*` that belong to a consent-region country:
 * Spain's Canary Islands and Ceuta, Portugal's Atlantic islands, Iceland, and
 * Norway's Svalbard.
 */
const EXTRA_REGION_ZONES = new Set([
  'Atlantic/Canary', 'Africa/Ceuta', 'Atlantic/Madeira', 'Atlantic/Azores',
  'Atlantic/Reykjavik', 'Arctic/Longyearbyen',
])

/** Is this time zone in (or near) a consent region? Over-inclusive by design. */
export const isConsentRegionTimeZone = (zone: string): boolean =>
  zone.startsWith('Europe/') || EXTRA_REGION_ZONES.has(zone)

/** Browser-side guess at whether this visitor is in a consent region. */
export const inConsentRegion = (): boolean => {
  try {
    return isConsentRegionTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? '')
  } catch {
    // No time zone available: ask. The safe direction.
    return true
  }
}
